// stores/transactionConfirmModal.ts

import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { Decimal } from "@/libs/decimal";
import { INTERACTIVE_VIEW } from "@/utils/constants";
import { TokenDto } from "@/api/generated";

export const useTokenTradeStore = defineStore("tokenTradeStore", () => {
  const token = ref<TokenDto>();
  const userBalance = ref<string | undefined>();

  const loadingAePrice = ref(false);
  const loadingTransaction = ref(false);
  const desiredSlippage = ref(0);

  const activeTradeView = ref<INTERACTIVE_VIEW>(INTERACTIVE_VIEW.BUY);
  const isBuying = computed(
    () => activeTradeView.value === INTERACTIVE_VIEW.BUY
  );
  const isUsingToken = computed(
    () => activeTradeView.value === INTERACTIVE_VIEW.SELL
  );
  const isAllowSelling = ref(false);

  const isSelling = computed(
    () => activeTradeView.value === INTERACTIVE_VIEW.SELL
  );

  const averageTokenPrice = computed<Decimal>(() => {
    if (!tokenA.value || !tokenB.value || tokenA.value <= 0) {
      return isBuying.value
        ? Decimal.from(token.value?.price || 0)
        : Decimal.from(token.value?.sell_price || 0);
    }
    const _tokenA = Decimal.from(tokenA.value);
    const _tokenB = Decimal.from(tokenB.value);
    return isBuying.value ? _tokenA.div(_tokenB) : _tokenB.div(_tokenA);
  });

  const currentTokenUnitPrice = computed(() => {
    return Decimal.from(token.value?.price || 0);
  });
  /**
   * The token A and token B values are used to store the token values
   * for the trade.
   */
  const tokenA = ref<number>();
  const tokenB = ref<number>();
  const tokenAFocused = ref<boolean>(false);

  const nextPrice = ref<Decimal>(Decimal.ZERO);
  const priceImpactDiff = computed(() => {
    if (!tokenA.value || !tokenB.value || tokenA.value <= 0) {
      return Decimal.ZERO;
    }
    const diff = nextPrice.value.sub(currentTokenUnitPrice.value);
    if (diff.lt(0) || isSelling.value) {
      return currentTokenUnitPrice.value.sub(
        Decimal.fromBigNumberString(nextPrice.value.bigNumber)
      );
    }
    return diff;
  });

  const priceImpactPercent = computed(() => {
    if (nextPrice.value.isZero || currentTokenUnitPrice.value.isZero) {
      return Decimal.ZERO;
    }

    // Calculate the percentage difference
    const percentageDifference = priceImpactDiff.value
      .div(currentTokenUnitPrice.value)
      .mul(100);

    return percentageDifference; // Return result with two decimal places
  });

  const estimatedNextTokenPriceImpactDifferenceFormattedPercentage = computed(
    () => {
      if (priceImpactPercent.value.gt(Decimal.from(1))) {
        return priceImpactPercent.value.prettify(2);
      }
      return priceImpactPercent.value.prettify();
    }
  );

  /**
   * Switches the active trade view and updates the token usage state.
   *
   * @param {INTERACTIVE_VIEW} view - The view to switch to. It should be one of the values from the INTERACTIVE_VIEW enum.
   * @returns {void}
   */
  function onSwitchTradeView(view: INTERACTIVE_VIEW) {
    activeTradeView.value = view;
  }

  function onReverseTradeView() {
    onSwitchTradeView(
      activeTradeView.value == INTERACTIVE_VIEW.BUY
        ? INTERACTIVE_VIEW.SELL
        : INTERACTIVE_VIEW.BUY
    );
  }

  return {
    tokenA,
    tokenB,
    tokenAFocused,
    averageTokenPrice,
    desiredSlippage,
    token,
    currentTokenUnitPrice,
    activeTradeView,
    isUsingToken,
    isSelling,
    isBuying,
    isAllowSelling,
    loadingAePrice,
    loadingTransaction,
    nextPrice,
    priceImpactDiff,
    priceImpactPercent,
    estimatedNextTokenPriceImpactDifferenceFormattedPercentage,
    userBalance,

    //
    onReverseTradeView,
    onSwitchTradeView,
  };
});
